import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarDays,
  faUserCheck,
  faUserXmark,
  faHourglassHalf,
  faPaperPlane,
  faTriangleExclamation,
  faRotate,
  faComments,
} from '@fortawesome/free-solid-svg-icons';
import { fmtServer } from './Servertime';
import './StudentPtm.css';

const API = process.env.REACT_APP_API_BASE_URL;

/* Meeting date: same as before (browser time) */
const fmtMeeting = (value) => {
  if (!value) return { day: '—', time: '' };
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { day: String(value), time: '' };
  return {
    day: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }),
    time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
  };
};

/* done   = Done 1 (parents came)
   absent = submitted, but parents weren't available
   open   = nothing submitted yet */
const stateOf = (ptm) => {
  if (Number(ptm.Done) === 1) return 'done';
  if (ptm.DateOfPtmDone) return 'absent';
  return 'open';
};

const STATE = {
  done: { label: 'Parents available', icon: faUserCheck, tone: 'is-good' },
  absent: { label: 'Parents not available', icon: faUserXmark, tone: 'is-bad' },
  open: { label: 'Not submitted yet', icon: faHourglassHalf, tone: 'is-wait' },
};

/* Same props as before: { studentId } */
const StudentPTM = ({ studentId }) => {
  const [ptms, setPtms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);

  // ---------- Load (same endpoint) ----------
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const response = await axios.get(`${API}/api/admin/students/ptm/${studentId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` },
        });
        if (!cancelled) setPtms(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error('Error fetching PTMs:', err);
        if (!cancelled) setError('We couldn’t load the parent meetings.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId, reload]);

  if (loading) {
    return (
      <div className="rmapt" aria-busy="true">
        <div className="rmapt-skel rmapt-skel--bar" />
        <div className="rmapt-skel" />
        <div className="rmapt-skel" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rmapt">
        <div className="rmapt-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
          <p>{error}</p>
          <button type="button" className="rmapt-btn" onClick={() => setReload((n) => n + 1)}>
            <FontAwesomeIcon icon={faRotate} /> Try again
          </button>
        </div>
      </div>
    );
  }

  if (ptms.length === 0) {
    return (
      <div className="rmapt">
        <div className="rmapt-empty">
          <FontAwesomeIcon icon={faComments} />
          <p>No parent meetings yet.</p>
        </div>
      </div>
    );
  }

  const sorted = [...ptms].sort((a, b) => new Date(b.DateOfPtm || 0) - new Date(a.DateOfPtm || 0));
  const count = (s) => ptms.filter((p) => stateOf(p) === s).length;

  return (
    <div className="rmapt">
      <ul className="rmapt-stats">
        <li>
          <b>{ptms.length}</b> meetings
        </li>
        <li className="is-good">
          <b>{count('done')}</b> parents came
        </li>
        <li className="is-bad">
          <b>{count('absent')}</b> not available
        </li>
        {count('open') > 0 && (
          <li className="is-wait">
            <b>{count('open')}</b> not submitted
          </li>
        )}
      </ul>

      <ol className="rmapt-list">
        {sorted.map((ptm, i) => {
          const st = STATE[stateOf(ptm)];
          const when = fmtMeeting(ptm.DateOfPtm);
          const submitted = fmtServer(ptm.DateOfPtmDone);
          return (
            <li key={ptm.Id ?? i} className={`rmapt-card ${st.tone}`}>
              <div className="rmapt-top">
                <div className="rmapt-when">
                  <i className="rmapt-ico">
                    <FontAwesomeIcon icon={faCalendarDays} />
                  </i>
                  <span>
                    <strong>{when.day}</strong>
                    {when.time && <small>{when.time}</small>}
                  </span>
                </div>
                <span className="rmapt-pill">
                  <FontAwesomeIcon icon={st.icon} /> {st.label}
                </span>
              </div>

              <div className="rmapt-notes">
                <h4>What was discussed</h4>
                <p className={ptm.Details ? '' : 'rmapt-dim'}>{ptm.Details || 'No details written.'}</p>
              </div>

              {submitted && (
                <p className="rmapt-sub">
                  <FontAwesomeIcon icon={faPaperPlane} /> Submitted {submitted} · Lebanon time
                </p>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default StudentPTM;